/**
 * Transient, dismissible notifications.
 *
 * Deliberately separate from `uiStore`: toasts are not preferences and must
 * not be persisted. Server state stays in TanStack Query and screen-local
 * state in `useState`; this exists only so unrelated components (a form and
 * the shell that renders the <Toaster/>) can agree on a short-lived message.
 */
import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => string;
  dismiss: (id: string) => void;
}

let counter = 0;

function nextId(): string {
  counter += 1;
  return `toast-${counter}`;
}

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  push: (message, tone = 'info') => {
    const id = nextId();
    set((state) => ({ toasts: [...state.toasts, { id, message, tone }] }));
    return id;
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

/**
 * Test-only: reset the store between cases so accumulated toasts from one
 * test don't leak into the next.
 */
export function __resetToastStoreForTests() {
  useToastStore.setState({ toasts: [] });
  counter = 0;
}

/** Convenience accessor used by screens, so they don't reach into the store. */
export function useToast() {
  const push = useToastStore((state) => state.push);
  const dismiss = useToastStore((state) => state.dismiss);

  return {
    success: (message: string) => push(message, 'success'),
    error: (message: string) => push(message, 'error'),
    info: (message: string) => push(message, 'info'),
    dismiss,
  };
}
